class TOSRotatingPlaceholder {
  constructor(textarea) {
    this.textarea = textarea;
    this.placeholder = textarea.parentElement?.querySelector('.tos-idea__rotating-placeholder');
    this.examples = [
      'I want a vintage oversized tee inspired by Japanese streetwear with a minimalist mountain illustration on the back...',
      "I'm creating matching T-shirts for my startup team. Clean, premium and minimal with our logo on the front...",
      "I have a rough idea, but I'd love your help bringing it to life...",
      'I want something inspired by ancient Indian history with a modern streetwear aesthetic...',
    ];
    this.index = 0;
    this.interval = null;
    this.isDisabled = false;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.placeholder) {
      return;
    }

    this.disable = this.disable.bind(this);
    this.textarea.addEventListener('focus', this.disable, { once: true });
    this.textarea.addEventListener('input', this.disable, { once: true });

    this.start();
  }

  start() {
    this.showExample(this.examples[0]);

    if (this.prefersReducedMotion) {
      return;
    }

    this.interval = window.setInterval(() => this.showNext(), 3600);
  }

  showNext() {
    if (this.isDisabled || this.textarea.value.trim()) {
      this.disable();
      return;
    }

    this.placeholder.classList.remove('is-visible');

    window.setTimeout(() => {
      if (this.isDisabled) {
        return;
      }

      this.index = (this.index + 1) % this.examples.length;
      this.showExample(this.examples[this.index]);
    }, 360);
  }

  showExample(example) {
    this.placeholder.textContent = example;
    requestAnimationFrame(() => {
      if (!this.isDisabled) {
        this.placeholder.classList.add('is-visible');
      }
    });
  }

  disable() {
    this.isDisabled = true;
    window.clearInterval(this.interval);
    this.placeholder.textContent = '';
    this.placeholder.classList.add('is-disabled');
    this.placeholder.classList.remove('is-visible');
  }
}

class TOSInspirationBoard {
  constructor(section) {
    this.section = section;
    this.input = section.querySelector('[data-inspiration-upload]');
    this.board = section.querySelector('[data-inspiration-board]');
    this.continueButton = section.querySelector('[data-inspiration-continue]');
    this.references = [];

    if (!this.input || !this.board) {
      return;
    }

    this.input.addEventListener('change', (event) => this.addFiles(event.currentTarget.files));
    this.updateState();
  }

  addFiles(fileList) {
    const files = Array.from(fileList || []).filter((file) => this.isAcceptedImage(file));

    files.forEach((file) => {
      const reference = {
        file,
        url: URL.createObjectURL(file),
      };

      this.references.push(reference);
      this.renderReference(reference, this.references.length - 1);
    });

    this.input.value = '';
    this.updateState();
  }

  isAcceptedImage(file) {
    return ['image/jpeg', 'image/png'].includes(file.type);
  }

  renderReference(reference, index) {
    const image = document.createElement('img');
    const placement = this.getPlacement(index);

    image.className = 'tos-idea__reference-card';
    image.src = reference.url;
    image.alt = reference.file.name.replace(/\.[^/.]+$/, '') || 'Inspiration reference';
    image.decoding = 'async';
    image.style.setProperty('--tos-ref-x', `${placement.x}%`);
    image.style.setProperty('--tos-ref-y', `${placement.y}%`);
    image.style.setProperty('--tos-ref-rotation', `${placement.rotation}deg`);

    reference.element = image;
    this.board.append(image);
  }

  getPlacement(index) {
    const placements = [
      { x: 26, y: 36 },
      { x: 52, y: 34 },
      { x: 72, y: 46 },
      { x: 36, y: 66 },
      { x: 62, y: 68 },
      { x: 18, y: 58 },
      { x: 82, y: 28 },
      { x: 46, y: 50 },
    ];
    const base = placements[index % placements.length];
    const cycle = Math.floor(index / placements.length);
    const rotation = this.randomBetween(-4, 4);

    return {
      x: Math.min(88, Math.max(12, base.x + cycle * 3)),
      y: Math.min(82, Math.max(18, base.y + cycle * 2)),
      rotation,
    };
  }

  randomBetween(min, max) {
    return Math.round((Math.random() * (max - min) + min) * 10) / 10;
  }

  updateState() {
    const hasReferences = this.references.length > 0;

    this.board.classList.toggle('has-references', hasReferences);

    if (this.continueButton) {
      this.continueButton.disabled = !hasReferences;
    }
  }

  getReferenceData() {
    return this.references.map((reference) => ({
      name: reference.file.name,
      url: reference.url,
    }));
  }

  destroy() {
    this.references.forEach((reference) => {
      URL.revokeObjectURL(reference.url);
    });
    this.references = [];
  }
}

class TOSIdeaJourney {
  constructor(section) {
    this.section = section;
    this.options = Array.from(section.querySelectorAll('input[name="tos_idea_product_type"]'));
    this.heroStage = section.querySelector('.tos-idea__hero-stage');
    this.storyTextarea = section.querySelector('[data-rotating-placeholder]');
    this.backButton = section.querySelector('[data-story-back]');
    this.continueButton = section.querySelector('[data-story-continue]');
    this.inspirationBackButton = section.querySelector('[data-inspiration-back]');
    this.inspirationContinueButton = section.querySelector('[data-inspiration-continue]');
    this.inspirationStep = section.querySelector('[data-inspiration-step]');
    this.reviewStep = section.querySelector('[data-review-step]');
    this.reviewBackButton = section.querySelector('[data-review-back]');
    this.reviewPayButton = section.querySelector('[data-review-pay]');
    this.contactFields = Array.from(section.querySelectorAll('[data-review-contact]'));
    this.reviewProduct = section.querySelector('[data-review-product]');
    this.reviewStory = section.querySelector('[data-review-story]');
    this.reviewCount = section.querySelector('[data-review-count]');
    this.reviewReferences = section.querySelector('[data-review-references]');
    this.inspirationBoard = null;
    this.designSlotVariantId = section.dataset.designSlotVariantId;
    this.designSlotProductHandle = section.dataset.designSlotProductHandle || 'design-slot-reservation';
    this.selectedProduct = null;
    this.isTransitioning = false;
    this.isStoryStep = false;
    this.isInspirationStep = false;
    this.isReviewStep = false;
    this.isSubmittingReservation = false;
    this.transitionableHandles = new Set(['oversized-tee', 'polo', 'hoodie', 'kidswear']);

    this.options.forEach((option) => {
      option.addEventListener('change', (event) => this.onSelect(event));
    });

    this.backButton?.addEventListener('click', () => this.exitStoryStep());
    this.continueButton?.addEventListener('click', () => this.enterInspirationStep());
    this.inspirationBackButton?.addEventListener('click', () => this.returnToStoryFromInspiration());
    this.inspirationContinueButton?.addEventListener('click', () => this.enterReviewStep());
    this.reviewBackButton?.addEventListener('click', () => this.returnToInspirationFromReview());
    this.reviewPayButton?.addEventListener('click', () => this.reserveDesignSlot());
    this.contactFields.forEach((field) => {
      field.addEventListener('input', () => this.updateReviewPayState());
    });
    this.storyTextarea?.addEventListener('input', () => this.updateContinueState());
    this.updateContinueState();
    this.updateReviewPayState();
    this.resolveDesignSlotVariant();
  }

  setInspirationBoard(inspirationBoard) {
    this.inspirationBoard = inspirationBoard;
  }

  onSelect(event) {
    this.selectProduct(event.currentTarget);

    if (this.isTransitioning || !this.transitionableHandles.has(this.selectedProduct.handle)) {
      return;
    }

    window.setTimeout(() => {
      this.promoteProduct();
      this.beginTransition();
    }, 300);
  }

  selectProduct(input) {
    const option = input.closest('.tos-idea__option');
    const illustration = option?.querySelector('[data-product-illustration]');

    this.selectedProduct = {
      handle: input.dataset.optionHandle,
      input,
      option,
      illustration,
      imageSrc: illustration?.currentSrc || illustration?.src || '',
      heroSrc: illustration?.dataset.productHero || illustration?.currentSrc || illustration?.src || '',
    };
  }

  beginTransition() {
    this.isTransitioning = true;
    this.section.classList.add('is-transitioning');

    this.options.forEach((option) => {
      option.disabled = option !== this.selectedProduct.input;
    });
  }

  promoteProduct() {
    if (!this.selectedProduct?.illustration || !this.selectedProduct.imageSrc || !this.heroStage) {
      this.enterStoryStep();
      return;
    }

    const heroAsset = this.createHeroProduct();
    const imageRect = this.selectedProduct.illustration.getBoundingClientRect();
    const stageRect = this.heroStage.getBoundingClientRect();

    const startLeft = imageRect.left - stageRect.left;
    const startTop = imageRect.top - stageRect.top;
    const deltaX = stageRect.width / 2 - (startLeft + imageRect.width / 2);
    const heroLift = window.innerHeight * (window.matchMedia('(max-width: 749px)').matches ? 0.22 : 0.18);
    const deltaY = stageRect.height / 2 - heroLift - (startTop + imageRect.height / 2);

    heroAsset.style.setProperty('--tos-hero-start-left', `${startLeft}px`);
    heroAsset.style.setProperty('--tos-hero-start-top', `${startTop}px`);
    heroAsset.style.setProperty('--tos-hero-start-width', `${imageRect.width}px`);
    heroAsset.style.setProperty('--tos-hero-delta-x', `${deltaX}px`);
    heroAsset.style.setProperty('--tos-hero-delta-y', `${deltaY}px`);
    heroAsset.style.setProperty('--tos-hero-scale', this.getHeroScale(imageRect));

    requestAnimationFrame(() => {
      this.section.classList.add('is-promoting');
    });

    window.setTimeout(() => this.enterStoryStep(), 1100);
  }

  createHeroProduct() {
    this.heroStage.querySelector('.tos-idea__hero-asset')?.remove();

    const asset = document.createElement('div');
    asset.className = 'tos-idea__hero-asset';

    const selectorImage = document.createElement('img');
    selectorImage.className = 'tos-idea__hero-image tos-idea__hero-image--selector';
    selectorImage.src = this.selectedProduct.imageSrc;
    selectorImage.alt = '';
    selectorImage.decoding = 'async';

    const heroImage = document.createElement('img');
    heroImage.className = 'tos-idea__hero-image tos-idea__hero-image--hero';
    heroImage.src = this.selectedProduct.heroSrc;
    heroImage.alt = '';
    heroImage.decoding = 'async';

    asset.append(selectorImage, heroImage);
    this.heroStage.prepend(asset);
    return asset;
  }

  getHeroScale(rect) {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const targetWidth = isMobile
      ? Math.min(window.innerWidth * 0.58, 300)
      : Math.min(window.innerWidth * 0.42, 520);

    return Math.max(1, targetWidth / rect.width).toFixed(3);
  }

  enterStoryStep() {
    this.isTransitioning = false;
    this.isStoryStep = true;
    this.isInspirationStep = false;
    this.isReviewStep = false;
    this.section.classList.add('is-transition-complete');
    this.section.classList.remove('is-inspiration-step', 'is-review-step');
    this.advanceProgress('concept');

    window.setTimeout(() => {
      this.section.classList.add('is-story-copy-visible');
    }, 420);

    window.setTimeout(() => {
      this.section.classList.add('is-story-textarea-visible');
      this.updateContinueState();
    }, 1100);
  }

  exitStoryStep() {
    if (!this.isStoryStep || this.isTransitioning || !this.selectedProduct?.illustration) {
      return;
    }

    this.isTransitioning = true;
    this.isStoryStep = false;
    this.section.classList.remove('is-story-textarea-visible', 'is-story-copy-visible', 'is-inspiration-step', 'is-review-step');
    this.advanceProgress('idea');

    window.setTimeout(() => {
      this.section.classList.remove('is-promoting');
    }, 260);

    window.setTimeout(() => {
      this.section.classList.remove('is-transitioning', 'is-transition-complete');
      this.heroStage?.querySelector('.tos-idea__hero-asset')?.remove();
      this.options.forEach((option) => {
        option.disabled = false;
        option.checked = false;
      });
      this.selectedProduct = null;
      this.isInspirationStep = false;
      this.isReviewStep = false;
      this.isTransitioning = false;
      this.updateContinueState();
    }, 1180);
  }

  enterInspirationStep() {
    if (!this.isStoryStep || this.isTransitioning || this.storyTextarea.value.trim().length < 8) {
      this.updateContinueState();
      return;
    }

    this.isStoryStep = false;
    this.isInspirationStep = true;
    this.isReviewStep = false;
    this.section.classList.add('is-inspiration-step');
    this.section.classList.remove('is-review-step');
    this.advanceProgress('design');
  }

  returnToStoryFromInspiration() {
    if (!this.isInspirationStep || this.isTransitioning) {
      return;
    }

    this.isInspirationStep = false;
    this.isStoryStep = true;
    this.section.classList.remove('is-inspiration-step', 'is-review-step');
    this.section.classList.add('is-story-copy-visible', 'is-story-textarea-visible');
    this.advanceProgress('concept');
    this.updateContinueState();
  }

  enterReviewStep() {
    if (!this.isInspirationStep || this.isTransitioning || !this.inspirationBoard?.references.length) {
      return;
    }

    this.populateReview();
    this.isInspirationStep = false;
    this.isReviewStep = true;
    this.section.classList.add('is-review-step');
    this.advanceProgress('delivered');
    this.updateReviewPayState();
  }

  returnToInspirationFromReview() {
    if (!this.isReviewStep || this.isTransitioning) {
      return;
    }

    this.isReviewStep = false;
    this.isInspirationStep = true;
    this.section.classList.remove('is-review-step');
    this.section.classList.add('is-inspiration-step');
    this.advanceProgress('design');
  }

  populateReview() {
    if (this.reviewProduct) {
      this.reviewProduct.textContent = this.selectedProduct?.input?.value || '-';
    }

    if (this.reviewStory) {
      this.reviewStory.textContent = this.storyTextarea?.value.trim() || '-';
    }

    const references = this.inspirationBoard?.getReferenceData() || [];

    if (this.reviewCount) {
      this.reviewCount.textContent = `${references.length} uploaded`;
    }

    if (this.reviewReferences) {
      this.reviewReferences.replaceChildren();

      references.slice(0, 6).forEach((reference) => {
        const image = document.createElement('img');
        image.className = 'tos-idea__review-reference';
        image.src = reference.url;
        image.alt = reference.name.replace(/\.[^/.]+$/, '') || 'Uploaded reference';
        image.decoding = 'async';
        this.reviewReferences.append(image);
      });
    }
  }

  updateContinueState() {
    if (!this.continueButton || !this.storyTextarea) {
      return;
    }

    this.continueButton.disabled = this.storyTextarea.value.trim().length < 8;
  }

  updateReviewPayState() {
    if (!this.reviewPayButton) {
      return;
    }

    const hasContactDetails = this.contactFields.every((field) => field.value.trim().length > 0);
    this.reviewPayButton.disabled = !hasContactDetails || !this.designSlotVariantId || this.isSubmittingReservation;

    if (!this.designSlotVariantId) {
      this.reviewPayButton.title = 'Create or select the Design Slot Reservation product first.';
    } else {
      this.reviewPayButton.removeAttribute('title');
    }
  }

  async resolveDesignSlotVariant() {
    if (this.designSlotVariantId || !this.designSlotProductHandle) {
      this.updateReviewPayState();
      return;
    }

    try {
      const response = await fetch(`/products/${encodeURIComponent(this.designSlotProductHandle)}.js`, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Design slot product was not found.');
      }

      const product = await response.json();
      const variant = product.variants?.find((item) => item.available) || product.variants?.[0];

      if (variant?.id) {
        this.designSlotVariantId = String(variant.id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.updateReviewPayState();
    }
  }

  getContactData() {
    const [nameField, emailField, phoneField] = this.contactFields;

    return {
      name: nameField?.value.trim() || '',
      email: emailField?.value.trim() || '',
      phone: phoneField?.value.trim() || '',
    };
  }

  getReservationProperties() {
    const contact = this.getContactData();
    const references = this.inspirationBoard?.getReferenceData() || [];

    return {
      'Canvas/Product selected': this.selectedProduct?.input?.value || '',
      'Story text': this.storyTextarea?.value.trim() || '',
      'Reference count': String(references.length),
      'Reference file names': references.map((reference) => reference.name).join(', '),
      'Customer name': contact.name,
      'Customer email': contact.email,
      'Phone / WhatsApp': contact.phone,
    };
  }

  async reserveDesignSlot() {
    if (this.reviewPayButton?.disabled || this.isSubmittingReservation) {
      return;
    }

    this.isSubmittingReservation = true;
    this.updateReviewPayState();
    this.reviewPayButton.textContent = 'Adding to checkout...';

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(this.designSlotVariantId),
              quantity: 1,
              properties: this.getReservationProperties(),
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to add design slot reservation to cart.');
      }

      window.location.href = '/checkout';
    } catch (error) {
      this.isSubmittingReservation = false;
      this.reviewPayButton.textContent = 'Reserve Design Slot — ₹499';
      this.updateReviewPayState();
      // Keep the user on the review step if Shopify rejects the add-to-cart request.
      console.error(error);
    }
  }

  advanceProgress(activeStep) {
    this.section.querySelectorAll('[data-progress-step]').forEach((step) => {
      step.classList.toggle('is-active', step.dataset.progressStep === activeStep);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const journeys = new Map();

  document.querySelectorAll('.tos-idea').forEach((section) => {
    journeys.set(section, new TOSIdeaJourney(section));
  });

  document.querySelectorAll('[data-rotating-placeholder]').forEach((textarea) => {
    new TOSRotatingPlaceholder(textarea);
  });

  document.querySelectorAll('.tos-idea').forEach((section) => {
    const inspirationBoard = new TOSInspirationBoard(section);
    journeys.get(section)?.setInspirationBoard(inspirationBoard);
  });
});
